/**
 * vertexFormat — the terrain vertex attribute layout, in one place, with the byte format of each.
 *
 * WHY BYTES. When this was written the chunk mesh was non-indexed (one vertex per triangle corner,
 * so the barycentric material blend worked) and a scene of ~3M triangles was 9M vertices a frame. As
 * seven Float32 attributes a vertex was 68 bytes — ~610 MB of vertex fetch per frame, which on an
 * integrated GPU with ~50 GB/s of shared memory is the frame. (The mesh is indexed now — see the
 * MeshGeometry header for the slot rule that allowed it — which cut the vertex count itself.) Measured on an Intel Iris Xe at 3.4 MP, Low preset:
 * the terrain material took 30 ms GPU; the same stream with a trivial fragment shader 25.5 ms; a
 * stock material reading only position+normal 13 ms. The fragment shader was 4.5 ms of the frame,
 * the vertex stream 12.5. So everything that fits a byte is a byte:
 *
 *   materialIds      u8 x4   integer material index (<= 63), read as a float in the shader
 *   materialWeights  u8 x4   normalized 0..1 — a one-hot basis vector, 255 is exactly 1.0
 *   lightLevel       u8      normalized 0..1 (5-bit source, so nothing is lost)
 *   blockLight       u8      normalized 0..1 (5-bit source)
 *   shoreFade        u8      normalized 0..1 (the shaders threshold it at 0.05)
 *   skirtKind        u8      integer enum, aprons only (SKIRT_KIND)
 *   lodLevel         u8      integer, absent at lod 0 (lodTexScale)
 *
 * The two 3-component byte attributes are FOUR components wide: D3D11 has no RGB8 vertex format, so
 * ANGLE would convert a 3-byte stride on the CPU; the shader still declares them `vec3` and reads
 * xyz. Positions stay Float32 (chunk-local; the batch's instance matrix places them) and normals stay
 * Float32 for now: the seam reconciliation averages them in place as floats.
 *
 * Importable from a worker — no Three.js objects here beyond the attribute constructor.
 */
import * as THREE from 'three';

export type AttrArray = Float32Array | Uint8Array;

/** Definition of a single vertex attribute: name, component count, and byte format. */
export interface AttrDef {
  name: string;
  itemSize: number;
  /** Backing store: 'f32' is a Float32Array, 'u8' a Uint8Array (one byte a component). */
  array: 'f32' | 'u8';
  /** For 'u8': the shader sees 0..255 mapped to 0..1 (true) or the raw integer (false). */
  normalized: boolean;
}

/** Components per vertex of materialIds / materialWeights (three used + one pad, see above). */
export const MATERIAL_SLOT_STRIDE = 4;

/**
 * Canonical vertex attribute layout for terrain meshes.
 * Must match MeshGeometry.expandGeometry; the TerrainBatch and the seam stitcher copy by it.
 */
export const TERRAIN_ATTRS: readonly AttrDef[] = [
  { name: 'position', itemSize: 3, array: 'f32', normalized: false },
  { name: 'normal', itemSize: 3, array: 'f32', normalized: false },
  { name: 'materialIds', itemSize: MATERIAL_SLOT_STRIDE, array: 'u8', normalized: false },
  { name: 'materialWeights', itemSize: MATERIAL_SLOT_STRIDE, array: 'u8', normalized: true },
  { name: 'lightLevel', itemSize: 1, array: 'u8', normalized: true },
  { name: 'blockLight', itemSize: 1, array: 'u8', normalized: true },
  // Wrap share — how much of a vertex sits against opaque geometry. The water and wind shaders pin
  // their displacement by it; on the solid layer it is always 0 (see SurfaceNetOutput.shoreFade).
  { name: 'shoreFade', itemSize: 1, array: 'u8', normalized: true },
];

/** Apron/skirt meshes only — not in TERRAIN_ATTRS so terrain meshes pay nothing for it. */
export const SKIRT_KIND_ATTR: AttrDef = { name: 'skirtKind', itemSize: 1, array: 'u8', normalized: false };

/** Allocate the backing array for `vertexCount` vertices of an attribute. */
export function allocAttrArray(def: AttrDef, vertexCount: number): AttrArray {
  const n = vertexCount * def.itemSize;
  return def.array === 'u8' ? new Uint8Array(n) : new Float32Array(n);
}

/** Wrap a backing array as the attribute its definition describes. */
export function bufferAttribute(def: AttrDef, arr: AttrArray): THREE.BufferAttribute {
  return new THREE.BufferAttribute(arr, def.itemSize, def.normalized);
}

/** 0..1 → the byte a normalized u8 attribute stores for it. */
export function unorm8(x: number): number {
  return x <= 0 ? 0 : x >= 1 ? 255 : Math.round(x * 255);
}
